/**
 * RitualMediaCarousel Component
 * Media carousel with navigation buttons and dot indicators
 * Supports both images and YouTube/Vimeo video embeds
 */

import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import { Pause, Play } from "lucide-react"
import * as React from "react"
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import { cn } from "@/lib"
import type { RitualMedia } from "../../types/detail.types"
import { RitualAudioPlayer } from "./RitualAudioPlayer"

interface RitualMediaCarouselProps {
  media: RitualMedia[]
  className?: string
}

export function RitualMediaCarousel({ media, className: _className }: RitualMediaCarouselProps) {
  const [api, setApi] = React.useState<CarouselApi>()
  const [current, setCurrent] = React.useState(0)
  const [count, setCount] = React.useState(0)
  const [playingIndex, setPlayingIndex] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!api) return

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
      // Stop video when navigating away from video slide
      setPlayingIndex(null)
    })
  }, [api])

  const placeholderCount = media.length > 0 ? media.length : 4

  // Pick the cover image to render behind audio slides. Prefers an explicit
  // isCover image, falls back to the first image in the list.
  const audioCoverImage = React.useMemo(() => {
    const explicitCover = media.find(m => m.type === "image" && m.isCover)
    if (explicitCover) return explicitCover.url
    const firstImage = media.find(m => m.type === "image")
    return firstImage?.url
  }, [media])

  const handlePlayVideo = (index: number) => {
    setPlayingIndex(index)
  }

  const handlePauseVideo = () => {
    setPlayingIndex(null)
  }

  return (
    <div className="w-full relative">
      <Carousel setApi={setApi} plugins={[WheelGesturesPlugin()]} className="w-full">
        {media.length > 1 && (
          <>
            <CarouselPrevious className="absolute left-5 md:left-18 z-20 md:h-10 md:w-10 lg:h-15 lg:w-15 border-1 border-white/20 bg-white/5 backdrop-blur-xs border-gradient-glass" />
            <CarouselNext className="absolute right-5 md:right-18 z-20 md:h-10 md:w-10 lg:h-15 lg:w-15 border-1 border-white/20 bg-white/5 backdrop-blur-xs border-gradient-glass" />
          </>
        )}
        <CarouselContent>
          {media.length > 0
            ? media.map((item, index) => (
                <CarouselItem key={item.id}>
                  <div className="max-h-[600px] w-full overflow-hidden relative min-[1445px]:rounded-[30px] bg-black">
                    {item.type === "image" ? (
                      <img
                        src={item.url}
                        alt={`Ritual media ${index + 1}`}
                        className="max-h-[600px] w-full object-cover min-[1445px]:rounded-[30px]"
                      />
                    ) : item.type === "audio" ? (
                      <RitualAudioPlayer
                        audioUrl={item.url}
                        coverImageUrl={item.thumbnailUrl || audioCoverImage}
                      />
                    ) : (
                      <div className="relative w-full h-[240px] md:h-[600px] min-[1445px]:rounded-[30px]">
                        {playingIndex === index ? (
                          <iframe
                            src={`${item.url}&autoplay=1`}
                            className="absolute inset-0 w-full h-full min-[1445px]:rounded-[30px]"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                          />
                        ) : (
                          <>
                            <img
                              src={item.thumbnailUrl || ""}
                              alt={item.title || "Video thumbnail"}
                              className="max-h-[600px] w-full object-cover min-[1445px]:rounded-[30px]"
                            />
                            <button
                              onClick={() => handlePlayVideo(index)}
                              className="absolute inset-0 flex items-center justify-center"
                              aria-label="Play video"
                            >
                              <div className="p-4 bg-black/50 rounded-full">
                                <Play className="h-10 w-10 text-white" fill="white" />
                              </div>
                            </button>
                          </>
                        )}
                        {/* Pause button when playing */}
                        {playingIndex === index && (
                          <button
                            onClick={handlePauseVideo}
                            className="absolute inset-0 flex items-center justify-center"
                            aria-label="Pause video"
                          >
                            <Pause className="h-8 w-8 text-white" fill="white" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))
            : Array.from({ length: placeholderCount }).map((_, index) => (
                <CarouselItem key={index}>
                  <div className="max-h-[600px] w-full overflow-hidden bg-card" />
                </CarouselItem>
              ))}
        </CarouselContent>
      </Carousel>

      {/* Dot Indicators - only show when multiple slides */}
      {media.length > 1 && (
        <div className="mb-[30px] flex items-center justify-center gap-2 absolute inset-x-0 bottom-0">
          {Array.from({ length: count || placeholderCount }).map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={cn(
                "h-1 w-8 rounded-none transition-all",
                index === current ? "bg-brand-offwhite" : "bg-brand-offwhite opacity-20",
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
